import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { SITEWIDE_RESULT_ICON } from "@search/api/constants";
import type { SitewideResult } from "@search/api/types";
import {
  getSitewideResultUrl,
  transformSitewideResults,
} from "@search/utils/sitewideResult";
import { useSitewideQuery } from "@sitewide/api/useSitewideQuery";
import clsx from "clsx";

import { Icon } from "@/booking/components/atoms/media/Icon";
import {
  Autocomplete,
  AutocompleteResultType,
} from "@/components/compounds/forms/Autocomplete";
import type { CommonProps } from "@/types";
import { createId } from "@/utils/helpers";

import styles from "./SitewideAutocomplete.module.css";

const ref = "sitewide-autocomplete";

interface SitewideAutocompleteProps extends Partial<CommonProps> {
  mobile?: boolean;
}

function render({ name, subtitle, type }: AutocompleteResultType) {
  const icon = type ? SITEWIDE_RESULT_ICON[type] : undefined;

  return (
    <div>
      <p>{name}</p>
      {subtitle && <p>{subtitle}</p>}
      {icon && <Icon name={icon} />}
    </div>
  );
}

function onSelect(item: AutocompleteResultType) {
  window.location.href = getSitewideResultUrl(item as SitewideResult);
}

function useSitewideAutocomplete(props: SitewideAutocompleteProps) {
  const { id = createId(ref), mobile = false } = props;
  const className = clsx(styles[ref], mobile && styles.nav, props.className);
  const { searchSitewide } = useSitewideQuery();

  return {
    className,
    color: props.color,
    iconColor: props.iconColor,
    id,
    searchSitewide,
  };
}

function SitewideAutocomplete(props: SitewideAutocompleteProps) {
  const { className, color, iconColor, id, searchSitewide } =
    useSitewideAutocomplete(props);

  async function search(text: string) {
    if (text) {
      const response = await searchSitewide(text);

      if (!response) {
        throw new Error(`Failed to fetch: "${text}"`);
      }

      return response?.data;
    }
  }

  return (
    <Autocomplete
      className={className}
      color={color}
      icon={faMagnifyingGlass}
      iconColor={iconColor}
      id={id}
      minLength={3}
      noResultsText={"No locations found"}
      placeholder={"Search for a destination or hotel..."}
      render={render}
      search={search}
      size={10} // TODO: This is a magic number, should be a constant
      transform={transformSitewideResults}
      onUpdate={onSelect}
    />
  );
}

export default SitewideAutocomplete;
export { SitewideAutocomplete, type SitewideAutocompleteProps };
